/**
 * TradeStack - Invoice Form Page (Create/Edit)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import '../styles/Forms.css';

function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { formatCurrency } = useConfig();

  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(isEditMode);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const customerId = searchParams.get('customerId');
  const jobId = searchParams.get('jobId');

  const [formData, setFormData] = useState({
    customer_id: customerId || '',
    job_id: jobId || '',
    due_days: 30,
    notes: ''
  });

  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: '', amount: 0 }
  ]);

  useEffect(() => {
    loadData();
    if (isEditMode) {
      loadInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const [customersRes, jobsRes] = await Promise.all([
        api.getCustomers({ limit: 1000 }),
        api.getJobs({ limit: 1000 })
      ]);
      setCustomers(customersRes.data.customers || []);
      setJobs(jobsRes.data.jobs || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const loadInvoice = async () => {
    try {
      const response = await api.getInvoice(id);
      const invoice = response.data.invoice;
      setFormData({
        customer_id: invoice.customer_id || '',
        job_id: invoice.job_id || '',
        due_days: invoice.due_days || 30,
        notes: invoice.notes || ''
      });
      if (invoice.line_items && invoice.line_items.length > 0) {
        setLineItems(invoice.line_items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || '',
          amount: item.amount || 0
        })));
      }
    } catch (err) {
      setError('Failed to load invoice');
      console.error(err);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;

    // Recalculate amount
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unit_price) || 0;
      updated[index].amount = qty * price;
    }

    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: '', amount: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.customer_id) {
      setError('Please select a customer');
      setLoading(false);
      return;
    }

    try {
      const submitData = {
        ...formData,
        customer_id: formData.customer_id,
        job_id: formData.job_id || null,
        line_items: lineItems.filter(item => item.description.trim()),
        subtotal: calculateTotal(),
        total: calculateTotal()
      };

      if (isEditMode) {
        await api.updateInvoice(id, submitData);
        navigate(`/invoices/${id}`);
      } else {
        const response = await api.createInvoice(submitData);
        navigate(`/invoices/${response.data.invoice.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} invoice`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvoice) {
    return <div className="form-page"><div className="loading-state">Loading...</div></div>;
  }

  return (
    <div className="form-page">
      <div className="detail-header">
        <div className="header-left">
          <Link to={isEditMode ? `/invoices/${id}` : '/invoices'} className="back-link">
            &larr; {isEditMode ? 'Back to Invoice' : 'Back to Invoices'}
          </Link>
          <h1>{isEditMode ? 'Edit Invoice' : 'New Invoice'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3>Invoice Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer_id">Customer *</label>
              <select
                id="customer_id"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                disabled={loadingData}
                required
              >
                <option value="">-- Select Customer --</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="job_id">Job (Optional)</label>
              <select
                id="job_id"
                name="job_id"
                value={formData.job_id}
                onChange={handleChange}
                disabled={loadingData}
              >
                <option value="">-- No Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="due_days">Payment Due (days)</label>
            <select
              id="due_days"
              name="due_days"
              value={formData.due_days}
              onChange={handleChange}
            >
              <option value="0">Due on Receipt</option>
              <option value="7">Net 7</option>
              <option value="14">Net 14</option>
              <option value="30">Net 30</option>
              <option value="45">Net 45</option>
              <option value="60">Net 60</option>
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Line Items</h3>
          <div className="line-items-table">
            <div className="line-item-header">
              <span className="col-description">Description</span>
              <span className="col-quantity">Qty</span>
              <span className="col-price">Unit Price</span>
              <span className="col-amount">Amount</span>
              <span className="col-actions"></span>
            </div>
            {lineItems.map((item, index) => (
              <div key={index} className="line-item-row">
                <input
                  type="text"
                  className="col-description"
                  value={item.description}
                  onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                  placeholder="Item description"
                />
                <input
                  type="number"
                  className="col-quantity"
                  value={item.quantity}
                  onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                  min="1"
                />
                <input
                  type="number"
                  className="col-price"
                  value={item.unit_price}
                  onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
                <span className="col-amount">{formatCurrency(item.amount)}</span>
                <button
                  type="button"
                  className="col-actions btn-icon"
                  onClick={() => removeLineItem(index)}
                  disabled={lineItems.length === 1}
                >
                  &times;
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addLineItem}>
              + Add Line Item
            </button>
          </div>

          <div className="invoice-total">
            <span className="total-label">Total Due:</span>
            <span className="total-value">{formatCurrency(calculateTotal())}</span>
          </div>
        </div>

        <div className="form-section">
          <h3>Notes</h3>
          <div className="form-group">
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Payment instructions or additional notes..."
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to={isEditMode ? `/invoices/${id}` : '/invoices'} className="btn btn-secondary">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Invoice')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default InvoiceForm;
